import React from "react";
import {
  ArrowLeftOutlined,
  CarOutlined,
  CreditCardOutlined,
  EditOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { Button, Typography } from "antd";
import PaymentCard from "../PaymentCard";
import { formatCurrency } from "../../../../utils/currency";
import ORDER_REVIEW from "./constants";
import useOrderReviewState from "./hooks/useOrderReviewState";
import { OrderReviewProps } from "./types";
import OrderSummary from "./components/OrderSummary";
import ProductThumb from "./components/ProductThumb";
import SectionCard from "./components/SectionCard";
import ShippingAddress from "./components/ShippingAddress";
import "./index.scss";

const { Text, Title } = Typography;

const OrderReview: React.FC<OrderReviewProps> = ({
  product,
  shippingDetails,
  totals,
  submitting,
  onBack,
  onEditShipping,
  onConfirm,
  onViewCard,
  setPaymentCardId,
}) => {
  const { hasPaymentCard, currency, onCardsLoaded } =
    useOrderReviewState(product);

  return (
    <div className="or-root">
      {/* ── Left: main content ── */}
      <div className="or-main-col">
        <div className="or-page-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            className="or-back-btn"
            onClick={onBack}
          >
            {ORDER_REVIEW.LABELS.BACK}
          </Button>
          <Button
            type="link"
            className="or-edit-shipping-btn"
            onClick={onEditShipping}
          >
            {ORDER_REVIEW.LABELS.EDIT_SHIPPING}
          </Button>
        </div>

        <Title level={2} className="or-page-title">
          {ORDER_REVIEW.LABELS.PAGE_TITLE}
        </Title>

        <SectionCard
          icon={<ShoppingOutlined />}
          title={ORDER_REVIEW.LABELS.SELECTED_ITEM}
        >
          <div className="or-product-row">
            <ProductThumb product={product} />
            <div className="or-product-info">
              <span className="or-product-name">{product.name}</span>
              <Text className="or-product-desc">{product.description}</Text>
            </div>
            <span className="or-product-price">
              {formatCurrency(totals.price, currency)}
            </span>
          </div>
        </SectionCard>

        <SectionCard
          icon={<CarOutlined />}
          title={ORDER_REVIEW.LABELS.SHIPPING_ADDRESS}
          action={
            <Button
              type="text"
              icon={<EditOutlined />}
              className="or-edit-btn"
              onClick={onEditShipping}
            >
              {ORDER_REVIEW.LABELS.EDIT}
            </Button>
          }
        >
          <ShippingAddress shippingDetails={shippingDetails} />
        </SectionCard>

        <SectionCard
          icon={<CreditCardOutlined />}
          title={ORDER_REVIEW.LABELS.PAYMENT_METHOD}
        >
          <PaymentCard
            handleViewCard={onViewCard}
            setPaymentCardId={setPaymentCardId}
            onCardsLoaded={onCardsLoaded}
          />
        </SectionCard>
      </div>

      {/* ── Right: order summary ── */}
      <div className="or-summary-col">
        <OrderSummary
          totals={totals}
          currency={currency}
          hasPaymentCard={hasPaymentCard}
          submitting={submitting}
          onConfirm={onConfirm}
        />
      </div>
    </div>
  );
};

export default OrderReview;
